/**
 * Sprite con người charforge: 6 kiểu khách + 12 NPC + 6 nhân vật chọn được
 * + 2 nhân viên. Phong cách chibi pixel nhất quán, tone cozy.
 *
 * Mỗi nhân vật = một lần gọi veNguoi() với tham số riêng (màu áo/tóc,
 * kiểu tóc, phụ kiện đặc trưng) nên 24 sprite khác nhau nhưng cùng ngôn ngữ
 * hình, không cần vẽ tay từng cái.
 */
import {
  BANG_MAU,
  Raster,
  bongPhai,
  duongThang,
  rect,
  sangLen,
  toiDi,
  tron,
  type VeSprite,
} from '../raster.js';

type KieuToc =
  'ngan' | 'dai' | 'bui' | 'hotoc' | 'khan' | 'non-la' | 'mu-bep' | 'mu-luoi-trai' | 'toc-buoc-cao';

type PhuKien = 'cap-sach' | 'cap-tap' | 'may-anh' | 'gay' | 'ro' | 'khay' | 'tui-vai' | null;

interface ThamSoNguoi {
  /** Màu nền sprite. */
  nen: string;
  da: string;
  toc: string;
  kieuToc: KieuToc;
  ao: string;
  quan: string;
  phuKien: PhuKien;
  /** Váy thay quần (nhân vật nữ). */
  vay?: string;
}

function veToc(r: Raster, o: ThamSoNguoi): void {
  const cx = 24;
  switch (o.kieuToc) {
    case 'ngan':
      // Mũ tóc ôm đầu.
      rect(r, cx - 10, 6, 20, 6, o.toc);
      rect(r, cx - 11, 10, 3, 8, o.toc);
      rect(r, cx + 8, 10, 3, 8, o.toc);
      rect(r, cx - 9, 5, 18, 2, sangLen(o.toc, 0.2));
      break;
    case 'dai':
      rect(r, cx - 10, 6, 20, 6, o.toc);
      rect(r, cx - 11, 10, 4, 20, o.toc);
      rect(r, cx + 7, 10, 4, 20, o.toc);
      rect(r, cx - 9, 5, 18, 2, sangLen(o.toc, 0.2));
      break;
    case 'bui':
      rect(r, cx - 10, 8, 20, 5, o.toc);
      // Gai nhọn.
      for (let i = 0; i < 5; i++) {
        const x = cx - 10 + i * 5;
        rect(r, x, 4, 3, 5, o.toc);
      }
      rect(r, cx - 11, 11, 3, 7, o.toc);
      rect(r, cx + 8, 11, 3, 7, o.toc);
      break;
    case 'hotoc':
      // Hói giữa, tóc hai bên.
      rect(r, cx - 11, 10, 4, 9, o.toc);
      rect(r, cx + 7, 10, 4, 9, o.toc);
      rect(r, cx - 11, 8, 4, 3, sangLen(o.toc, 0.15));
      rect(r, cx + 7, 8, 4, 3, sangLen(o.toc, 0.15));
      break;
    case 'khan':
      // Khăn đóng: dải ngang trán + nút thắt.
      rect(r, cx - 10, 9, 20, 5, o.toc);
      rect(r, cx + 8, 6, 5, 4, toiDi(o.toc, 0.8));
      break;
    case 'non-la':
      // Nón lá: hình nón + quai.
      for (let i = 0; i < 9; i++) {
        const w = 26 - i * 2;
        rect(r, cx - Math.floor(w / 2), 2 + i, w, 1, i % 2 === 0 ? '#d9b64f' : '#c9a53f');
      }
      rect(r, cx - 13, 11, 26, 1, '#8a6f2a');
      duongThang(r, cx - 8, 12, cx - 4, 22, '#8a6f2a');
      duongThang(r, cx + 8, 12, cx + 4, 22, '#8a6f2a');
      break;
    case 'mu-bep':
      // Mũ bếp trắng phồng.
      rect(r, cx - 8, 1, 16, 8, BANG_MAU.trang);
      rect(r, cx - 10, 5, 20, 5, BANG_MAU.trang);
      rect(r, cx - 9, 9, 18, 2, '#d9d9d9');
      rect(r, cx - 8, 1, 16, 2, sangLen(BANG_MAU.trang, 0.1));
      break;
    case 'mu-luoi-trai':
      rect(r, cx - 9, 5, 18, 6, o.toc);
      rect(r, cx - 9, 10, 24, 2, o.toc); // Lưỡi trai.
      rect(r, cx - 2, 5, 4, 6, sangLen(o.toc, 0.25));
      break;
    case 'toc-buoc-cao':
      rect(r, cx - 10, 7, 20, 5, o.toc);
      rect(r, cx - 11, 11, 3, 8, o.toc);
      rect(r, cx + 8, 11, 3, 8, o.toc);
      // Đuôi tóc buộc cao.
      rect(r, cx + 6, 0, 6, 8, o.toc);
      rect(r, cx - 2, 1, 4, 2, '#d94f70'); // Nơ.
      break;
  }
}

function vePhuKien(r: Raster, o: ThamSoNguoi): void {
  const cx = 24;
  switch (o.phuKien) {
    case 'cap-sach':
      rect(r, cx + 11, 28, 7, 11, '#8a5a2e');
      rect(r, cx + 11, 28, 7, 3, '#a8703a');
      break;
    case 'cap-tap':
      rect(r, cx + 13, 36, 8, 7, '#4a3728');
      rect(r, cx + 15, 34, 4, 3, '#4a3728');
      rect(r, cx + 13, 36, 8, 2, '#6b4f38');
      break;
    case 'may-anh':
      rect(r, cx - 5, 32, 10, 7, BANG_MAU.den);
      tron(r, cx, 35, 3, '#3a6ea5');
      tron(r, cx, 35, 1, '#9fd0f0');
      duongThang(r, cx - 5, 32, cx - 8, 27, '#555555');
      duongThang(r, cx + 5, 32, cx + 8, 27, '#555555');
      break;
    case 'gay':
      rect(r, cx + 14, 24, 2, 20, '#6b4a2a');
      rect(r, cx + 12, 22, 6, 3, '#6b4a2a');
      break;
    case 'ro':
      // Rổ tre đeo trước bụng.
      rect(r, cx - 8, 34, 16, 7, '#c9a53f');
      for (let i = 0; i < 4; i++) rect(r, cx - 8 + i * 4, 34, 1, 7, '#a8832f');
      rect(r, cx - 8, 34, 16, 2, '#d9b64f');
      break;
    case 'khay':
      rect(r, cx - 12, 34, 10, 2, '#8a8a8a');
      rect(r, cx - 10, 30, 3, 4, '#d94f4f');
      rect(r, cx - 6, 30, 3, 4, '#4f9fd9');
      break;
    case 'tui-vai':
      rect(r, cx - 16, 30, 6, 9, '#e8d9a0');
      rect(r, cx - 16, 30, 6, 2, '#d0b878');
      duongThang(r, cx - 13, 30, cx - 6, 26, '#b89a5a');
      break;
    case null:
      break;
  }
}

function veNguoi(r: Raster, o: ThamSoNguoi): void {
  const cx = 24;
  r.nen(o.nen);
  // Bóng đổ.
  rect(r, cx - 9, 45, 18, 2, BANG_MAU.bong);
  rect(r, cx - 6, 47, 12, 1, BANG_MAU.bong);
  if (o.vay) {
    // Váy + chân.
    rect(r, 21, 40, 2, 5, o.da);
    rect(r, 26, 40, 2, 5, o.da);
    // Váy xòe.
    for (let i = 0; i < 8; i++) {
      rect(r, cx - 8 - i, 33 + i, 16 + i * 2, 1, i % 2 === 0 ? o.vay : toiDi(o.vay, 0.9));
    }
  } else {
    // Quần + giày.
    rect(r, 20, 40, 4, 5, o.quan);
    rect(r, 25, 40, 4, 5, o.quan);
    rect(r, 19, 44, 6, 2, BANG_MAU.den);
    rect(r, 24, 44, 6, 2, BANG_MAU.den);
  }
  // Thân áo.
  rect(r, 17, 27, 14, 14, o.ao);
  rect(r, 17, 27, 14, 2, sangLen(o.ao, 0.2));
  bongPhai(r, 17, 27, 14, 14, toiDi(o.ao, 0.8));
  // Tay.
  rect(r, 13, 28, 4, 10, o.ao);
  rect(r, 31, 28, 4, 10, o.ao);
  rect(r, 13, 37, 4, 3, o.da);
  rect(r, 31, 37, 3, 3, o.da);
  // Cổ + đầu.
  rect(r, 22, 24, 4, 4, toiDi(o.da, 0.92));
  tron(r, cx, 16, 9, o.da);
  tron(r, cx - 3, 13, 4, sangLen(o.da, 0.12)); // Highlight má.
  // Mắt + miệng.
  r.px(20, 16, BANG_MAU.den);
  r.px(28, 16, BANG_MAU.den);
  r.px(21, 16, BANG_MAU.den);
  r.px(27, 16, BANG_MAU.den);
  rect(r, 22, 20, 4, 1, '#a85a3a');
  // Má hồng.
  r.px(18, 18, '#e89a8a');
  r.px(30, 18, '#e89a8a');
  veToc(r, o);
  vePhuKien(r, o);
}

/** 24 sprite con người: id manifest → hàm vẽ. */
export const VE_CON_NGUOI: Record<string, VeSprite> = {
  // --- 6 kiểu khách ---
  'khach-hoc-sinh': (r) =>
    veNguoi(r, {
      nen: '#7fb3e8',
      da: BANG_MAU.daSang,
      toc: BANG_MAU.tocDen,
      kieuToc: 'ngan',
      ao: '#3f7fbf',
      quan: '#2f3a45',
      phuKien: 'cap-sach',
    }),
  'khach-van-phong': (r) =>
    veNguoi(r, {
      nen: '#9aa5b1',
      da: BANG_MAU.daSang,
      toc: BANG_MAU.tocNau,
      kieuToc: 'ngan',
      ao: '#2f3a45',
      quan: '#1f282f',
      phuKien: 'cap-tap',
    }),
  'khach-du-lich': (r) =>
    veNguoi(r, {
      nen: '#f0b35e',
      da: BANG_MAU.daTrung,
      toc: BANG_MAU.tocVang,
      kieuToc: 'mu-luoi-trai',
      ao: '#c96a3b',
      quan: '#7a5a3a',
      phuKien: 'may-anh',
    }),
  'khach-lon-tuoi': (r) =>
    veNguoi(r, {
      nen: '#b8a88e',
      da: BANG_MAU.daTrung,
      toc: BANG_MAU.tocBac,
      kieuToc: 'hotoc',
      ao: '#6e5a44',
      quan: '#4a3d2e',
      phuKien: 'gay',
    }),
  'khach-dau-bep': (r) =>
    veNguoi(r, {
      nen: '#e8e8e8',
      da: BANG_MAU.daSang,
      toc: BANG_MAU.tocDen,
      kieuToc: 'mu-bep',
      ao: '#ffffff',
      quan: '#3a3a3a',
      phuKien: 'tui-vai',
    }),
  'khach-dem-khuya': (r) =>
    veNguoi(r, {
      nen: '#4a4a6e',
      da: BANG_MAU.daSang,
      toc: BANG_MAU.tocDen,
      kieuToc: 'dai',
      ao: '#2e2e4e',
      quan: '#1e1e32',
      phuKien: null,
      vay: '#3e3e5e',
    }),

  // --- 12 NPC ---
  'npc-chu-ba': (r) =>
    veNguoi(r, {
      nen: '#e8a84f',
      da: BANG_MAU.daTrung,
      toc: BANG_MAU.tocDen,
      kieuToc: 'khan',
      ao: '#c96a3b',
      quan: '#5e3a1a',
      phuKien: 'ro', // Chú Ba Nước Ngọt: rổ chai.
    }),
  'npc-co-sau': (r) =>
    veNguoi(r, {
      nen: '#c39bd3',
      da: BANG_MAU.daSang,
      toc: BANG_MAU.tocDen,
      kieuToc: 'toc-buoc-cao',
      ao: '#8e44ad',
      quan: '#4a2a5e',
      phuKien: 'tui-vai',
      vay: '#9b59b6',
    }),
  'npc-anh-tam': (r) =>
    veNguoi(r, {
      nen: '#7fd69b',
      da: BANG_MAU.daToi,
      toc: BANG_MAU.tocDen,
      kieuToc: 'bui',
      ao: '#27ae60',
      quan: '#1e6e3e',
      phuKien: 'ro', // Anh Tám Tươi: rổ rau.
    }),
  'npc-ba-tu': (r) =>
    veNguoi(r, {
      nen: '#f0a0b0',
      da: BANG_MAU.daTrung,
      toc: BANG_MAU.tocBac,
      kieuToc: 'dai',
      ao: '#d94f70',
      quan: '#7a2a3e',
      phuKien: 'ro',
      vay: '#e86a85', // Bà Tư Xôi.
    }),
  'npc-anh-hai': (r) =>
    veNguoi(r, {
      nen: '#8fb8e8',
      da: BANG_MAU.daToi,
      toc: BANG_MAU.tocDen,
      kieuToc: 'mu-luoi-trai',
      ao: '#2f6db3',
      quan: '#2a2a2a',
      phuKien: 'cap-tap', // Anh Hai Xe Ôm.
    }),
  'npc-co-ba': (r) =>
    veNguoi(r, {
      nen: '#7fd6d6',
      da: BANG_MAU.daSang,
      toc: BANG_MAU.tocNau,
      kieuToc: 'non-la',
      ao: '#16a3a3',
      quan: '#0e5a5a',
      phuKien: 'ro',
      vay: '#3fbfbf', // Cô Ba Biển.
    }),
  'npc-chu-sau': (r) =>
    veNguoi(r, {
      nen: '#a0b0c8',
      da: BANG_MAU.daToi,
      toc: BANG_MAU.tocBac,
      kieuToc: 'hotoc',
      ao: '#5b6e8a',
      quan: '#3a4a5e',
      phuKien: 'gay', // Chú Sáu Lưới.
    }),
  'npc-em-ut': (r) =>
    veNguoi(r, {
      nen: '#f0d67f',
      da: BANG_MAU.daSang,
      toc: BANG_MAU.tocDen,
      kieuToc: 'toc-buoc-cao',
      ao: '#b3892e',
      quan: '#6e5620',
      phuKien: 'tui-vai',
      vay: '#d9b64f', // Em Út Đêm.
    }),
  'npc-di-nam': (r) =>
    veNguoi(r, {
      nen: '#e8a0a8',
      da: BANG_MAU.daTrung,
      toc: BANG_MAU.tocDen,
      kieuToc: 'khan',
      ao: '#b34a5e',
      quan: '#6e2a38',
      phuKien: 'khay',
      vay: '#c65e70', // Dì Năm Hủ Tiếu: khay.
    }),
  'npc-bac-tu': (r) =>
    veNguoi(r, {
      nen: '#c8b89a',
      da: BANG_MAU.daTrung,
      toc: BANG_MAU.tocBac,
      kieuToc: 'ngan',
      ao: '#6e5a44',
      quan: '#4a3d2e',
      phuKien: 'gay', // Bác Tư Tổ Trưởng.
    }),
  'npc-chi-hong': (r) =>
    veNguoi(r, {
      nen: '#f0b8d0',
      da: BANG_MAU.daSang,
      toc: BANG_MAU.tocNau,
      kieuToc: 'dai',
      ao: '#c65e85',
      quan: '#7a3a55',
      phuKien: 'tui-vai',
      vay: '#d67a9a', // Chị Hồng Homestay.
    }),
  'npc-ong-chin': (r) =>
    veNguoi(r, {
      nen: '#8a9ab0',
      da: BANG_MAU.daToi,
      toc: BANG_MAU.tocBac,
      kieuToc: 'mu-luoi-trai',
      ao: '#3a4a5e',
      quan: '#242e3a',
      phuKien: 'gay', // Ông Chín Bảo Vệ.
    }),

  // --- 6 nhân vật chọn được ---
  'nv-hoa': (r) =>
    veNguoi(r, {
      nen: '#f0b35e',
      da: BANG_MAU.daSang,
      toc: BANG_MAU.tocDen,
      kieuToc: 'toc-buoc-cao',
      ao: '#d94f4f',
      quan: '#7a2a2a',
      phuKien: null,
      vay: '#e86a6a',
    }),
  'nv-minh': (r) =>
    veNguoi(r, {
      nen: '#7fb3e8',
      da: BANG_MAU.daTrung,
      toc: BANG_MAU.tocDen,
      kieuToc: 'bui',
      ao: '#3f7fbf',
      quan: '#2a4a6e',
      phuKien: null,
    }),
  'nv-lam': (r) =>
    veNguoi(r, {
      nen: '#7fd6d6',
      da: BANG_MAU.daSang,
      toc: BANG_MAU.tocNau,
      kieuToc: 'dai',
      ao: '#16a3a3',
      quan: '#0e5a5a',
      phuKien: null,
      vay: '#3fbfbf',
    }),
  'nv-duc': (r) =>
    veNguoi(r, {
      nen: '#9aa5b1',
      da: BANG_MAU.daToi,
      toc: BANG_MAU.tocDen,
      kieuToc: 'ngan',
      ao: '#5b6e8a',
      quan: '#2f3a45',
      phuKien: null,
    }),
  'nv-thao': (r) =>
    veNguoi(r, {
      nen: '#f0d67f',
      da: BANG_MAU.daSang,
      toc: BANG_MAU.tocVang,
      kieuToc: 'ngan',
      ao: '#b3892e',
      quan: '#6e5620',
      phuKien: null,
    }),
  'nv-phuc': (r) =>
    veNguoi(r, {
      nen: '#c8b89a',
      da: BANG_MAU.daTrung,
      toc: BANG_MAU.tocBac,
      kieuToc: 'hotoc',
      ao: '#6e5a44',
      quan: '#4a3d2e',
      phuKien: null,
    }),

  // --- 2 nhân viên ---
  'nhan-vien-thu-ngan': (r) =>
    veNguoi(r, {
      nen: '#8fb8e8',
      da: BANG_MAU.daSang,
      toc: BANG_MAU.tocDen,
      kieuToc: 'ngan',
      ao: '#2e7fbf',
      quan: '#1f4a6e',
      phuKien: 'khay',
    }),
  'nhan-vien-xep-ke': (r) =>
    veNguoi(r, {
      nen: '#8fd6a8',
      da: BANG_MAU.daTrung,
      toc: BANG_MAU.tocNau,
      kieuToc: 'khan',
      ao: '#3fae6a',
      quan: '#2a6e44',
      phuKien: 'ro',
    }),
};
